import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { TextField, Autocomplete, autocompleteClasses, TextFieldProps, Chip } from '@mui/material';

export type Option = { key: string | number; label: string };

interface ListboxComponentProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  onEndReached?: () => void;
}

const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>(
  ({ children, onEndReached, ...other }, ref) => {
    const items = React.Children.toArray(children);

    return (
      <div
        ref={ref}
        {...other}
        style={{
          height: items.length > 0 ? Math.min(items.length * 48, 300) : 0,
        }}
      >
        <Virtuoso
          style={{ height: '100%' }}
          overscan={10}
          data={items}
          itemContent={(index, item) => item as React.ReactElement}
          computeItemKey={(index, item) => {
            const element = item as React.ReactElement;
            return element.key != null ? String(element.key) : `option-${index}`;
          }}
          endReached={() => onEndReached?.()}
          increaseViewportBy={{ top: 0, bottom: 200 }}
        />
      </div>
    );
  }
);

function renderOptionWithStableKey(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: Option,
) {
  return (
    <li {...props} key={String(option.key)}>
      {option.label}
    </li>
  );
}

interface VirtualizedAutocompleteProps {
  // value: string | number | null | undefined;
  selectedOption?: Option | null;
  onChange: (value: Option | null) => void;
  options: Option[];
  label?: string;
  onEndReached?: () => void;
  textFieldProps?: TextFieldProps;
  onTextChange?: (text: string | null) => void; // called when user types
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

export function VirtualizedAutocomplete({
  selectedOption,
  onChange,
  options,
  label,
  onEndReached,
  textFieldProps,
  onTextChange,
  disabled,
  isLoading,
  loadingText,
}: VirtualizedAutocompleteProps) {
  const optionsWithSelectedOnTop = useMemo(() => [selectedOption, ...options.filter((option) => option.key !== selectedOption?.key)].filter(Boolean) as Option[], [options, selectedOption]);

  return (
    <Autocomplete<Option, false, false, false>
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(newValue ? { key: newValue.key, label: newValue.label } : null);
        onTextChange?.(null);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' && onTextChange) {
          onTextChange?.(newInputValue);
        }
      }}
      onClose={() => {
        if (!selectedOption && onTextChange) {
          onTextChange?.(null);
        }
      }}
      options={optionsWithSelectedOnTop}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => val && option.key === val?.key}
      renderOption={renderOptionWithStableKey}
      ListboxComponent={ListboxComponent}
      ListboxProps={{
        onEndReached,
      } as any}
      renderInput={(params) => <TextField {...params} {...textFieldProps} label={label} />}
      disabled={disabled}
      sx={{
        [`& .${autocompleteClasses.listbox}`]: {
          padding: 0,
        },
      }}
      noOptionsText={isLoading ? loadingText : undefined}
    />
  );
}

interface VirtualizedAutocompleteMultipleProps {
  value: (string | number)[] | null | undefined;
  onChange: (value: (string | number)[] | null | undefined) => void;
  options: Option[];
  label?: string;
  onEndReached?: () => void;
  textFieldProps?: TextFieldProps;
  onTextChange?: (text: string | null) => void; // called when user types
  disabled?: boolean;
  limitTags?: number; // limit number of chips displayed, shows "+n" for remaining
  popperWidth?: number; // custom width for the dropdown popper
  hideSelectionWhenClosed?: boolean;
  /** When true, selected options are pinned to the top of the dropdown list */
  pinSelectedOnTop?: boolean;
  /** When true, search is handled server-side via onTextChange (disables client filtering) */
  serverSideSearch?: boolean;
  /** When true, the dropdown stays open after selecting an option */
  disableCloseOnSelect?: boolean;
}

export function VirtualizedAutocompleteMultiple({
  value,
  onChange,
  options,
  label,
  onEndReached,
  textFieldProps,
  onTextChange,
  disabled,
  limitTags = -1,
  popperWidth,
  hideSelectionWhenClosed = false,
  pinSelectedOnTop = false,
  serverSideSearch = false,
  disableCloseOnSelect = false,
}: VirtualizedAutocompleteMultipleProps) {
  const [inputValue, setInputValue] = useState('');

  const uniqueOptions = useMemo(() => {
    const seen = new Set<string | number>();
    return options.filter((option) => {
      if (seen.has(option.key)) {
        return false;
      }
      seen.add(option.key);
      return true;
    });
  }, [options]);

  const selectedOptions = useMemo(
    () => uniqueOptions.filter((option) => value?.includes(option.key)) || [],
    [uniqueOptions, value]
  );

  const listOptions = useMemo(() => {
    if (!pinSelectedOnTop || !value?.length) {
      return uniqueOptions;
    }

    const selectedKeys = new Set(value);
    const selected = Array.from(new Set(value))
      .map((key) => uniqueOptions.find((option) => option.key === key))
      .filter((option): option is Option => Boolean(option));
    const unselected = uniqueOptions.filter((option) => !selectedKeys.has(option.key));

    return [...selected, ...unselected];
  }, [uniqueOptions, pinSelectedOnTop, value]);

  const handleClose = () => {
    setInputValue('');

    if (!onTextChange) {
      return;
    }

    if (serverSideSearch || !value?.length) {
      onTextChange(null);
    }
  };

  return (
    <Autocomplete<Option, true, false, false>
      multiple
      disableCloseOnSelect={disableCloseOnSelect}
      value={selectedOptions}
      {...(serverSideSearch ? { inputValue } : {})}
      onChange={(_, newValues) => {
        const keys = newValues.map((v) => v.key);
        onChange(keys.length > 0 ? keys : null);

        if (!serverSideSearch) {
          onTextChange?.(null);
        }
      }}
      {...(serverSideSearch
        ? {
            onInputChange: (_: unknown, newInputValue: string, reason: string) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
                onTextChange?.(newInputValue);
                return;
              }

              if (reason === 'clear') {
                setInputValue('');
                onTextChange?.(null);
              }
            },
            clearOnBlur: false,
            filterOptions: onTextChange ? (filteredOptions: Option[]) => filteredOptions : undefined,
          }
        : {})}
      onClose={handleClose}
      options={listOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => val && option.key === val?.key}
      renderOption={renderOptionWithStableKey}
      ListboxComponent={ListboxComponent}
      ListboxProps={{
        onEndReached,
      } as any}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          {...(!serverSideSearch
            ? {
                value: inputValue,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  setInputValue(event.target.value);
                },
                inputProps: {
                  ...params.inputProps,
                  value: inputValue,
                },
                InputProps: {
                  ...params.InputProps,
                  ...textFieldProps?.InputProps,
                  value: inputValue,
                },
              }
            : {
                InputLabelProps: {
                  ...params.InputLabelProps,
                  ...textFieldProps?.InputLabelProps,
                  ...(hideSelectionWhenClosed ? { shrink: inputValue.length > 0 } : {}),
                },
              })}
          label={label}
        />
      )}
      renderTags={
        hideSelectionWhenClosed
          ? () => null
          : (tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.key}
                  label={option.label}
                  color="info"
                  variant="soft"
                  size="small"
                />
              ))
      }
      disabled={disabled}
      limitTags={limitTags}
      slotProps={{
        popper: popperWidth
          ? {
              style: { width: `${popperWidth}px` },
            }
          : undefined,
      }}
      sx={{
        [`& .${autocompleteClasses.listbox}`]: {
          padding: 0,
        },
      }}
    />
  );
}
